'use client';

import { useScopeTree } from '@/hooks/use-scope-tree';

import { Stack } from '@slims/ui/atoms';
import { AiCoachChat, type AiCoachMessage } from '@slims/ui/organisms';
import { apiFetch } from '@/lib/utils/api';
import { useState } from 'react';
import { AiRecommendationsPanel } from '@/components/myslims/ai-recommendations-panel';
import { DailyRecommendations } from '@/components/myslims/daily-recommendations';
import type { AdminRec, RecCtx } from '@/lib/admin-recs';
import { createSupabaseBrowser } from '@/lib/supabase/client';

type ChatMessage = AiCoachMessage;

function newId() {
	return typeof crypto !== 'undefined' && crypto.randomUUID
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random()}`;
}

interface AiSuggestionsSidebarProps {
	cerNome?: string;
	ruolo?: string;
	/** Dati CER per le raccomandazioni (KPI/contesto chat). */
	recCtx: RecCtx;
	/** Identificativo per persistere lo stato Sì/No delle raccomandazioni. */
	tenantId: string;
	/** Energia condivisa del mese (kWh). */
	condivisa?: number;
}

export function AiSuggestionsSidebar({
	cerNome,
	ruolo,
	recCtx,
	tenantId,
	condivisa = 0,
}: AiSuggestionsSidebarProps) {
	const { leaves } = useScopeTree();
	const leaf = leaves.find((s) => s.tenantId === tenantId);
	const orgId = leaf?.orgId;
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [input, setInput] = useState('');
	const [streaming, setStreaming] = useState(false);

	// "Spiega con l'AI Coach": mostra subito la risposta pre-scritta della
	// raccomandazione (parità con l'app mobile), poi resta disponibile per
	// domande di follow-up via /api/chat.
	function explainRec(rec: AdminRec) {
		setMessages((prev) => [
			...prev,
			{ id: newId(), role: 'user', text: `Spiegami: ${rec.label}` },
			{ id: newId(), role: 'assistant', text: rec.response },
		]);
	}

	async function sendMessage(text: string) {
		const trimmed = text.trim();
		if (!trimmed || streaming) return;

		const nextMessages: ChatMessage[] = [...messages, { id: newId(), role: 'user', text: trimmed }];
		setMessages(nextMessages);
		setInput('');
		setStreaming(true);

		try {
			const supabase = createSupabaseBrowser();
			const {
				data: { session },
			} = await supabase.auth.getSession();
			const token = session?.access_token;
			if (!token) throw new Error('no-session');

			const res = await apiFetch(`/api/chat?orgId=${encodeURIComponent(orgId ?? '')}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ messages: nextMessages, cerNome, ruolo }),
			});

			if (!res.ok || !res.body) throw new Error('request-failed');

			const assistantId = newId();
			setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', text: '' }]);
			const reader = res.body.getReader();
			const decoder = new TextDecoder();
			let acc = '';
			while (true) {
				const { done, value } = await reader.read();
				if (done) break;
				const chunk = decoder.decode(value, { stream: true });
				if (chunk.includes('\x00ERROR')) throw new Error('stream-error');
				acc += chunk;
				setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, text: acc } : m)));
			}
		} catch {
			setMessages((prev) => [
				...prev,
				{
					id: newId(),
					role: 'assistant',
					text: "Non riesco a rispondere in questo momento. Riprova più tardi o contatta l'amministratore CER.",
				},
			]);
		} finally {
			setStreaming(false);
		}
	}

	return (
		<Stack as="aside" gap="md">
			{/* Il piano del giorno calcolato sui dati del socio (INB-355). Il
			    catalogo sotto compare solo in modalità demo, come «Esempio». */}
			{orgId && leaf?.features?.aiSuggestions === 'enabled' && (
				<DailyRecommendations orgId={orgId} compact />
			)}
			<AiRecommendationsPanel
				tenantId={tenantId}
				ctx={recCtx}
				condivisa={condivisa}
				onExplain={explainRec}
			/>

			{/* AI Coach chat */}
			<AiCoachChat
				messages={messages}
				input={input}
				onInputChange={setInput}
				onSend={sendMessage}
				streaming={streaming}
			/>
		</Stack>
	);
}
